import React, { useEffect, useMemo, useState } from 'react';

import { Table } from 'antd';
import Axios from 'axios';

import { capitalize } from '@libs/NwUtils';

import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import { forceReloadInvoices } from '@hooks/API/UseInvoices';
import { forceReloadPayments } from '@hooks/API/UsePayments';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import EmptyState from '@components/Gui/EmptyState';
import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import {
    buildInvoiceDraftData,
    getUniqueBillingInfoIds,
    hasMixedCurrenciesForInvoice,
    resolveSelectedCurrencyFromStatements,
    sumTaxedStatementsTotal,
} from '@components/StatementRowsCommon/helpers';
import { adaptPayableRows } from '@components/StatementRowsCommon/adapters';

import { StatementDrawers } from '../StatementCommon';
import { useStatementContext, useStatementDispatch } from '../StatementContext';
import { StatementTableContainer } from '../StatementUI';
import PayableActions from './PayableActions';
import {
    getPayableColumns,
    getInvoiceTypeIcon,
    getInvoiceTypeTooltip,
    getPaymentTooltip,
    getStatementActions,
} from './PayableColumns';
import {
    isTransactionInvoiced,
    isTransactionInvoicePosted,
    isTransactionPaid,
    isTransactionInNetwalk3Payment,
    isTransactionPrevisional,
    isTransactionHold,
    isTransactionNw3Locked,
    getTableClassName,
    getRowClassName,
} from './PayableHelpers';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Statements that go into a model invoice: JobFee statements only as commissions or advances
const isStatementInvoiceable = (statement, family) =>
    !(family === 'JobFee' && !statement.isCommission && !statement.isClientAdvance);

/**
 * PayableBody
 *
 * Table body component for payable statements.
 * Displays transactions with multi-selection and actions.
 */
const PayableBody = ({
    hideTotal,
    isPrevisional,
    isHold,
    transactions,
    statementTotals,
    billingInfoColorMap,
    afterStatementReload,
    rowSelectionEnabled = false,
    selectedRowKeys = [],
    onSelectedRowKeysChange,
}) => {
    const userGuiConfig = useUserGuiConfig();
    const configuration = useConfigurations();
    let numberformat = 'USD0,0.00';
    if (userGuiConfig && userGuiConfig.numbers && userGuiConfig.numbers.format) {
        numberformat = userGuiConfig.numbers.format;
    }

    const { contactID, contactType, statementType, expand, showSubjectNameInTitle, scope, statementFiltered } =
        useStatementContext();
    const dispatch = useStatementDispatch();

    const onStatementReload = () => {
        dispatch({ type: 'StartReload' });
        if (afterStatementReload) {
            afterStatementReload();
        }
    };

    // State for drawers and selections
    const [selectedTransaction, setSelectedTransaction] = useState();
    const [showTransactionDetailDrawer, setShowTransactionDetailDrawer] = useState(false);
    const [docVersion, setDocVersion] = useState();
    const [paymentData, setPaymentData] = useState();
    const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
    const [invoiceData, setInvoiceData] = useState();
    const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
    const [addToInvoiceData, setAddToInvoiceData] = useState(null);
    const [selectedList, setSelectedList] = useState([]);
    const [disableInvoiceButton, setDisableInvoiceButton] = useState(true);
    const [invoiceButtonErrors, setInvoiceButtonErrors] = useState([]);
    const [disablePaymentButton, setDisablePaymentButton] = useState(true);
    const [paymentButtonErrors, setPaymentButtonErrors] = useState([]);
    const [disableRemoveTaxesButton, setDisableRemoveTaxesButton] = useState(true);
    const [removeTaxesButtonErrors, setRemoveTaxesButtonErrors] = useState([]);
    const [serviceId, setServiceId] = useState();
    const [isServiceDrawer, setIsServiceDrawer] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [selectedStatementsTotal, setSelectedStatementsTotal] = useState(0);
    const [expandedItems, setExpandedItems] = useState([]);
    const [transactionWasModified, setTransactionWasModified] = useState(false);

    const configurations = useConfigurations();

    const handleCloseInvoiceDrawer = () => {
        onStatementReload();
        setSelectedInvoice(null);
    };

    const handleClosePaymentDrawer = () => {
        onStatementReload();
        setSelectedPayment(null);
    };

    useEffect(() => {
        setSelectedList([]);
    }, [transactions]);

    const isItemExpanded = (itemID) => {
        return expand || expandedItems.includes(itemID);
    };

    const clearSelectedList = () => {
        setSelectedList([]);
        setSelectedStatementsTotal('');
    };

    const handlePaymentCreation = (paymentID) => {
        forceReloadPayments(contactID);
        setShowPaymentDrawer(false);
        setPaymentData(null);
        clearSelectedList();
        setSelectedPayment(paymentID);
    };

    const handleInvoiceCreation = (invoiceID) => {
        forceReloadInvoices(contactID);
        setShowInvoiceDrawer(false);
        setInvoiceData(null);
        clearSelectedList();
        setSelectedInvoice(invoiceID);
    };

    const handleRowsAddedToInvoice = (invoiceID) => {
        forceReloadInvoices(contactID);
        setAddToInvoiceData(null);
        clearSelectedList();
        setSelectedInvoice(invoiceID);
    };

    const handleCloseAddToInvoiceDrawer = (someRowsAdded) => {
        setAddToInvoiceData(null);
        if (someRowsAdded) {
            forceReloadInvoices(contactID);
            onStatementReload();
        }
    };

    const handleCloseDetailDrawer = () => {
        setShowTransactionDetailDrawer(false);
        setSelectedTransaction(null);
        if (transactionWasModified) {
            onStatementReload();
            setTransactionWasModified(false);
        }
    };

    const handleEditTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionDetailDrawer(true);
        setTransactionWasModified(false);
    };

    const handlePreviewService = (serviceID) => {
        setServiceId(serviceID);
        setIsServiceDrawer(true);
    };

    const handleCloseServiceDrawer = () => {
        setServiceId();
        setIsServiceDrawer(false);
    };

    const [projectPreviewID, setProjectPreviewID] = useState();

    const handlePreviewProject = (projectID) => {
        setProjectPreviewID(projectID);
    };

    const handleCloseProjectDrawer = () => {
        onStatementReload();
        setProjectPreviewID();
    };

    const setTransactionStatementsHoldStatus = async (record, holdStatus) => {
        for (const statement of record.Statements) {
            await Axios.put(`/transactions/${record.Family}/modifystatementonhold`, {
                StatementID: statement.ID,
                OnHold: holdStatus,
            });
        }
        onStatementReload();
    };

    const hasMultipleBillingInfos = billingInfoColorMap && Object.keys(billingInfoColorMap).length > 1;
    const displayedTransactions = useMemo(() => adaptPayableRows(transactions), [transactions]);

    // Get columns configuration
    const columns = getPayableColumns({
        contactType,
        statementType,
        expand,
        showSubjectNameInTitle,
        hasMultipleBillingInfos,
        billingInfoColorMap,
        isItemExpanded,
        handleEditTransaction,
        handlePreviewService,
        handlePreviewProject,
        getInvoiceTypeIcon: (transaction) =>
            getInvoiceTypeIcon(transaction, isTransactionInvoicePosted, byPrefixAndName),
        getInvoiceTypeTooltip: (transaction) => getInvoiceTypeTooltip(transaction, contactType),
        getPaymentTooltip: (record) => getPaymentTooltip(record, contactType),
        isTransactionInvoiced,
        isTransactionPaid,
        isTransactionInNetwalk3Payment,
        isTransactionHold,
        isTransactionNw3Locked,
        onStatusInvoiceClose: onStatementReload,
        onStatusPaymentClose: onStatementReload,
        setSelectedPayment,
        getStatementActions: (record) =>
            getStatementActions(
                record,
                handleEditTransaction,
                setTransactionStatementsHoldStatus,
                isTransactionHold,
                byPrefixAndName
            ),
    });

    const handleCreatePayment = () => {
        setShowPaymentDrawer(false);
        const selectedTransactions = displayedTransactions.filter((tr) => selectedList.includes(tr.ID));
        const selectedStatementsID = selectedTransactions.map((tr) => tr.Statements.map((st) => st.ID)).flat();
        const selectedStatements = selectedTransactions.map((tr) => tr.Statements).flat();

        for (const tr of selectedTransactions) {
            if (tr.Currency !== configurations.defaultCurrency) {
                if (tr.Statements.some((st) => st.AmountDef === 0 && st.Amount !== 0)) {
                    alert('Please add a conversion value for transactions with different currency');
                    return;
                }
            }
        }
        const uniquebillinginfosIDs = getUniqueBillingInfoIds(selectedStatements);
        if (uniquebillinginfosIDs.length !== 1) {
            alert('Please select transactions with the same billing info');
            return;
        }
        const billingInfoID = selectedStatements[0].BillingInfoID;

        const paymentType = contactType.charAt(0).toUpperCase() + contactType.slice(1);

        const ret = {
            Type: paymentType,
            Currency: configurations.defaultCurrency,
            BillingInfoID: billingInfoID,
            ContactID: contactID,
            StatementIDs: selectedStatementsID,
        };
        setPaymentData(ret);
        setShowPaymentDrawer(true);
    };

    const handleCreateInvoice = async () => {
        setShowInvoiceDrawer(false);
        const selectedTransactions = displayedTransactions.filter((tr) => selectedList.includes(tr.ID));
        const selectedStatements = selectedTransactions.flatMap((transaction) => transaction.Statements);
        const uniquebillinginfosIDs = getUniqueBillingInfoIds(selectedStatements);
        if (uniquebillinginfosIDs.length !== 1) {
            alert('Please select transactions with the same billing info');
            return;
        }

        const contactsBillingInfos = await Axios.get(`/contacts/billinginfos/${contactID}`);
        const selectedBillingInfo = contactsBillingInfos.find((bi) => bi.ID === uniquebillinginfosIDs[0]);
        const invoiceType = capitalize(contactType);

        const ret = buildInvoiceDraftData({
            statements: selectedStatements,
            contactID,
            contactName: getContactName(contactID),
            contactTypeLabel: invoiceType,
            billingInfo: selectedBillingInfo,
            defaultCurrency: configurations.defaultCurrency,
            agencyFeeCount: 0,
            getFamily: (statement) => statement.TransactionFamily,
            includeStatement: isStatementInvoiceable,
        });

        setInvoiceData(ret);
        setShowInvoiceDrawer(true);
    };

    // Same billing info, currency and statement rules as invoice creation, applied to an existing draft invoice
    const handleAddToInvoice = () => {
        const selectedTransactions = displayedTransactions.filter((tr) => selectedList.includes(tr.ID));
        const selectedStatements = selectedTransactions.flatMap((transaction) => transaction.Statements);
        const uniquebillinginfosIDs = getUniqueBillingInfoIds(selectedStatements);
        if (uniquebillinginfosIDs.length !== 1) {
            alert('Please select transactions with the same billing info');
            return;
        }

        const rows = selectedTransactions.flatMap((transaction) =>
            transaction.Statements.filter((statement) =>
                isStatementInvoiceable(statement, statement.TransactionFamily)
            ).map((statement) => ({ StatementID: statement.ID, Title: transaction.Title }))
        );

        setAddToInvoiceData({
            ContactID: contactID,
            Type: capitalize(contactType),
            BillingInfoID: uniquebillinginfosIDs[0],
            Currency: resolveSelectedCurrencyFromStatements(selectedStatements, configurations.defaultCurrency),
            HasMixedCurrencies: hasMixedCurrenciesForInvoice(selectedStatements, configurations.defaultCurrency),
            Rows: rows,
        });
    };

    const handleRemoveTaxes = () => {
        const selectedTransactions = displayedTransactions.filter((tr) => selectedList.includes(tr.ID));
        const selectedStatementIDs = selectedTransactions.map((tr) => tr.Statements.map((st) => st.ID)).flat();
        Axios.put(`/transactions/removetaxes`, { StatementIDs: selectedStatementIDs })
            .then((res) => {
                onStatementReload();
            })
            .catch((err) => {
                console.error('err', err);
            });
    };

    const checkSelectedRows = (selectedRows) => {
        let disableInvoice = false;
        let disablePayment = false;
        let disableRemoveTaxes = false;
        const selectedStatements = selectedRows.flatMap((tr) => tr.Statements);

        if (selectedRows.length) {
            const defCur = configuration.defaultCurrency;
            setSelectedStatementsTotal(sumTaxedStatementsTotal(selectedStatements, defCur, numberformat));
        } else {
            setSelectedStatementsTotal('');
        }

        const paymentErrors = [];
        const invoiceErrors = [];
        const removeTaxesErrors = [];
        const notInvoiceable = ['JobRefund', 'JobAdvance'];

        for (const tr of selectedRows) {
            if (isTransactionPaid(tr)) {
                disablePayment = true;
                paymentErrors.push(`Line "${tr.Title}" is already paid`);
                disableRemoveTaxes = true;
                removeTaxesErrors.push(`Line "${tr.Title}" is already paid`);
                setSelectedStatementsTotal('');
            }
            if (tr.Statements.some((st) => st.InvoiceID)) {
                invoiceErrors.push(`Line "${tr.Title}" is already invoiced`);
                disableInvoice = true;
            }
            if (tr.Family) {
                if (notInvoiceable.includes(tr.Family)) {
                    invoiceErrors.push(`Line "${tr.Title}" is a ${tr.Family} and can't be invoiced`);
                    disableInvoice = true;
                }
            }
        }

        // Same currency rule as billing and add to invoice: one invoice, one currency
        if (hasMixedCurrenciesForInvoice(selectedStatements, configuration.defaultCurrency)) {
            invoiceErrors.push('Selected lines have different currencies: convert them to the default currency');
            disableInvoice = true;
        }

        setPaymentButtonErrors(paymentErrors);
        setInvoiceButtonErrors(invoiceErrors);
        setRemoveTaxesButtonErrors(removeTaxesErrors);
        setDisablePaymentButton(disablePayment);
        setDisableInvoiceButton(disableInvoice);
        setDisableRemoveTaxesButton(disableRemoveTaxes);
    };

    const searchRowSelection = rowSelectionEnabled
        ? {
              selectedRowKeys,
              onChange: (keys) => {
                  if (onSelectedRowKeysChange) {
                      onSelectedRowKeysChange(keys);
                  }
              },
          }
        : null;

    const rowSelection = {
        selectedRowKeys: selectedList,
        onChange: (selectedRowKeys, selectedRows) => {
            checkSelectedRows(selectedRows);
            setSelectedList(selectedRowKeys);
        },
        getCheckboxProps: (record) => {
            return {
                disabled:
                    isTransactionPrevisional(record) ||
                    isTransactionHold(record) ||
                    isTransactionNw3Locked(record) ||
                    (isTransactionPaid(record) && isTransactionInvoiced(record)) ||
                    record.isPayment,
            };
        },
    };

    return (
        <StatementTableContainer $search={scope === 'search'}>
            {displayedTransactions.length > 0 ? (
                <>
                    <Table
                        rowSelection={
                            searchRowSelection || (scope === 'statement' ? rowSelection : undefined)
                        }
                        showSizeChanger
                        pagination={false}
                        columns={columns}
                        dataSource={displayedTransactions}
                        className={getTableClassName(isPrevisional, isHold)}
                        rowClassName={(record) => getRowClassName(record, isTransactionPaid) + ' cursor-pointer'}
                        summary={(pageData) => {
                            const summaryColSpan = scope === 'statement' ? 6 : 5;
                            if (hideTotal || statementFiltered) {
                                return null;
                            }
                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={summaryColSpan}></Table.Summary.Cell>
                                    <Table.Summary.Cell>
                                        <strong style={{ display: 'block', textAlign: 'right' }}>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell>
                                        {statementTotals.map(
                                            (item) =>
                                                (item.transactionCount > 0 || item.isDefaultCurrency) && (
                                                    <Money
                                                        key={item.Currency}
                                                        dineroObj={item.Dinero}
                                                        redIfNegative
                                                        boldIfDefaultCurrency
                                                        style={{ display: 'block', textAlign: 'right' }}
                                                    />
                                                )
                                        )}
                                        <small style={{ display: 'block', textAlign: 'right', fontSize: '0.75rem' }}>
                                            DISPLAYED ROWS SUBTOTAL
                                        </small>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell>
                                        <div className="summary-icon-wrapper">
                                            <NwIcon icon={byPrefixAndName.fass['calculator']} />
                                        </div>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                    {scope === 'statement' && selectedList.length > 0 && (
                        <PayableActions
                            selectedList={selectedList}
                            disableInvoiceButton={disableInvoiceButton}
                            disablePaymentButton={disablePaymentButton}
                            disableRemoveTaxesButton={disableRemoveTaxesButton}
                            invoiceButtonErrors={invoiceButtonErrors}
                            paymentButtonErrors={paymentButtonErrors}
                            removeTaxesButtonErrors={removeTaxesButtonErrors}
                            selectedStatementsTotal={selectedStatementsTotal}
                            handleCreateInvoice={handleCreateInvoice}
                            handleAddToInvoice={handleAddToInvoice}
                            handleCreatePayment={handleCreatePayment}
                            handleRemoveTaxes={handleRemoveTaxes}
                        />
                    )}
                </>
            ) : (
                <EmptyState message="No Results" />
            )}
            <StatementDrawers
                showTransactionDetailDrawer={showTransactionDetailDrawer}
                selectedTransaction={selectedTransaction}
                handleCloseDetailDrawer={handleCloseDetailDrawer}
                onUpdateTransaction={() => {
                    if (scope !== 'search') {
                        onStatementReload();
                    } else {
                        setTransactionWasModified(true);
                    }
                }}
                onTransactionRemoved={() => {
                    setShowTransactionDetailDrawer(false);
                    setSelectedTransaction(null);
                    onStatementReload();
                    if (selectedTransaction.Project?.ID) {
                        forceReloadProjectTransactions(selectedTransaction.Project.ID);
                        forceReloadProject(selectedTransaction.Project.ID);
                    }
                }}
                docVersion={docVersion}
                setDocVersion={setDocVersion}
                isServiceDrawer={isServiceDrawer}
                serviceId={serviceId}
                handleCloseServiceDrawer={handleCloseServiceDrawer}
                showPaymentDrawer={showPaymentDrawer}
                setShowPaymentDrawer={setShowPaymentDrawer}
                paymentData={paymentData}
                handlePaymentCreation={handlePaymentCreation}
                selectedPayment={selectedPayment}
                handleClosePaymentDrawer={handleClosePaymentDrawer}
                contactType={contactType}
                showInvoiceDrawer={showInvoiceDrawer}
                setShowInvoiceDrawer={setShowInvoiceDrawer}
                invoiceData={invoiceData}
                handleInvoiceCreation={handleInvoiceCreation}
                selectedInvoice={selectedInvoice}
                handleCloseInvoiceDrawer={handleCloseInvoiceDrawer}
                addToInvoiceData={addToInvoiceData}
                handleCloseAddToInvoiceDrawer={handleCloseAddToInvoiceDrawer}
                handleRowsAddedToInvoice={handleRowsAddedToInvoice}
                projectPreviewID={projectPreviewID}
                handleCloseProjectDrawer={handleCloseProjectDrawer}
            />
        </StatementTableContainer>
    );
};

export default PayableBody;
